import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { ILayerAction, ILayerParam, IPalletTypeItem, IStackAction, IStackParam } from './types';

type FormValidators = Array<(control: AbstractControl) => ValidationErrors | null>;

@Injectable({
	providedIn: 'root',
})
export class PalletisingConfigurationViewModel {
	public isLoadingConfiguration = false;
	public isUpdatingConfiguration = false;
	public tableColumns: GenericTableColumn[];
	public layersColumns: GenericTableColumn[];
	public palletTypeItemTableColumns: GenericTableColumn[];
	public tableSource: MatTableDataSource<IStackParam>;
	public layersTableSource: MatTableDataSource<ILayerParam>;
	public palletTypes: IPalletTypeItem[];
	public selectedPalletType: IPalletTypeItem | null = null;
	public elementPalletTypes: IPalletTypeItem[];
	public filteredAssociatedTypes: IPalletTypeItem[];
	public rowActions: IStackAction[];
	public layerRowActions: ILayerAction[];
	public allowStackingWithoutSupportingBase = true;
	public palletElementPadding = 0;
	public palletMaxHeight = 0;
	public palletMaxLayers = 0;
	public palletMaxRows = 0;
	public rotateMaxLengthToHeightAspectRatio = 0;
	public rotateMaxWeight = 0;
	public rotateMinRatioSingleElementOnly = true;

	// forms
	public form: FormGroup;
	public sortingPriorityForm: FormGroup;
	public poolingPriorityForm: FormGroup;

	public formFieldsLookup: { property: string; control: AbstractControl }[];
	public sortingFormFieldsLookup: { property: string; control: AbstractControl }[];
	public poolingFormFieldsLookup: { property: string; control: AbstractControl }[];

	// sorting
	public sortingElementLength = false;
	public sortingElementLengthOrder = false;
	public sortingElementLengthPriority: number | null;
	public sortingPositionWeight = false;
	public sortingPositionWeightOrder = false;
	public sortingPositionWeightPriority: number | null;
	public sortingBatchNumber = false;
	public sortingBatchNumberOrder = false;
	public sortingBatchNumberPriority: number | null;
	public sortingProductionSequenceNumber = false;
	public sortingProductionSequenceNumberOrder = false;
	public sortingProductionSequenceNumberPriority: number | null;
	// pooling
	public poolingPallet = false;
	public poolingPalletUnspecified = false;
	public poolingPalletPriority: number | null;
	public poolingBatch = false;
	public poolingBatchUnspecified = false;
	public poolingBatchPriority: number | null;
	public poolingOrder = false;
	public poolingOrderUnspecified = false;
	public poolingOrderPriority: number | null;
	public poolingPartition = false;
	public poolingPartitionUnspecified = false;
	public poolingPartitionPriority: number | null;
	public poolingPalletDimension = false;
	public poolingPalletDimensionUnspecified = false;
	public poolingPalletDimensionPriority: number | null;
	public poolingPalletType = false;
	public poolingPalletTypeUnspecified = false;
	public poolingPalletTypePriority: number | null;
	public poolingGlassFitting = false;
	public poolingGlassFittingUnspecified = false;
	public poolingGlassFittingPriority: number | null;

	// The 4 possible methods, -1=None, 0=Uniform, 1=Edge, 2=Center, 0 Uniform is default.
	public balancingMethod: '-1' | '0' | '1' | '2' = '-1';

	public constructor(private fb: FormBuilder) {
		this.tableColumns = [
			{
				name: 'masterdata.palletising.configuration.stackParam.toHeight',
				dataKey: 'toHeight',
			},
			{
				name: 'masterdata.palletising.configuration.stackParam.maxHeightAfter',
				dataKey: 'maxHeightAfter',
			},
			{
				name: 'masterdata.palletising.configuration.stackParam.maxWeightElement',
				dataKey: 'maxWeightElement',
			},
		];
		this.layersColumns = [
			{ name: 'masterdata.palletising.configuration.layers.layer', dataKey: 'layer' },
			{
				name: 'masterdata.palletising.configuration.layers.maxHeightAfter',
				dataKey: 'maxHeightAfter',
			},
			{
				name: 'masterdata.palletising.configuration.layers.maxWeightElement',
				dataKey: 'maxWeightElement',
			},
		];
		this.palletTypeItemTableColumns = [{ name: 'Name', dataKey: 'name' }];
		this.rowActions = [
			{ title: 'masterdata.edit', handler: 'editStackParam' },
			{ title: 'masterdata.delete', handler: 'deleteStackParam' },
		];
		this.layerRowActions = [
			{ title: 'masterdata.edit', handler: 'editLayerParam' },
			{ title: 'masterdata.delete', handler: 'deleteLayerParam' },
		];
		this.tableSource = new MatTableDataSource<IStackParam>();
		this.layersTableSource = new MatTableDataSource<ILayerParam>();
		this.palletTypes = [];
		this.filteredAssociatedTypes = [];
		this.initializeForms();
		this.setFormFieldsLookupMaps();
	}

	private setFormFieldsLookupMaps(): void {
		const form = this.form;
		const sortForm = this.sortingPriorityForm;
		const poolForm = this.poolingPriorityForm;
		this.formFieldsLookup = [];
		this.formFieldsLookup.push({
			property: 'palletMaxRows',
			control: form.get('palletMaxRows'),
		});
		this.formFieldsLookup.push({
			property: 'palletMaxLayers',
			control: form.get('palletMaxLayers'),
		});
		this.formFieldsLookup.push({
			property: 'palletMaxHeight',
			control: form.get('palletMaxHeight'),
		});
		this.formFieldsLookup.push({
			property: 'palletElementPadding',
			control: form.get('palletElementPadding'),
		});
		this.formFieldsLookup.push({
			property: 'rotateMaxWeight',
			control: form.get('rotateMaxWeight'),
		});
		this.formFieldsLookup.push({
			property: 'rotateMaxLengthToHeightAspectRatio',
			control: form.get('rotateMaxLengthToHeightAspectRatio'),
		});
		this.sortingFormFieldsLookup = [];
		this.sortingFormFieldsLookup.push({
			property: 'sortingElementLength',
			control: sortForm.get('sortingElementLengthPriority'),
		});
		this.sortingFormFieldsLookup.push({
			property: 'sortingPositionWeight',
			control: sortForm.get('sortingPositionWeightPriority'),
		});
		this.sortingFormFieldsLookup.push({
			property: 'sortingBatchNumber',
			control: sortForm.get('sortingBatchNumberPriority'),
		});
		this.sortingFormFieldsLookup.push({
			property: 'sortingProductionSequenceNumber',
			control: sortForm.get('sortingProductionSequenceNumberPriority'),
		});
		this.poolingFormFieldsLookup = [];
		this.poolingFormFieldsLookup.push({
			property: 'poolingPallet',
			control: poolForm.get('poolingPalletPriority'),
		});
		this.poolingFormFieldsLookup.push({
			property: 'poolingBatch',
			control: poolForm.get('poolingBatchPriority'),
		});
		this.poolingFormFieldsLookup.push({
			property: 'poolingOrder',
			control: poolForm.get('poolingOrderPriority'),
		});
		this.poolingFormFieldsLookup.push({
			property: 'poolingPartition',
			control: poolForm.get('poolingPartitionPriority'),
		});
		this.poolingFormFieldsLookup.push({
			property: 'poolingPalletDimension',
			control: poolForm.get('poolingPalletDimensionPriority'),
		});
		this.poolingFormFieldsLookup.push({
			property: 'poolingPalletType',
			control: poolForm.get('poolingPalletTypePriority'),
		});
		this.poolingFormFieldsLookup.push({
			property: 'poolingGlassFitting',
			control: poolForm.get('poolingGlassFittingPriority'),
		});
	}

	private get formValidators(): FormValidators {
		return [Validators.required, Validators.min(0), Validators.max(2_147_483_647)];
	}

	private get sortingPriorityFormValidators(): FormValidators {
		return [this.sortingNotUnique.bind(this), Validators.max(2_147_483_647)];
	}

	private get poolingPriorityFormValidators(): FormValidators {
		return [this.poolingNotUnique.bind(this), Validators.max(2_147_483_647)];
	}

	private initializeForms(): void {
		this.form = this.fb.group({
			palletMaxRows: [this.palletMaxRows, this.formValidators],
			palletMaxLayers: [this.palletMaxLayers, this.formValidators],
			palletMaxHeight: [this.palletMaxHeight, this.formValidators],
			palletElementPadding: [this.palletElementPadding, this.formValidators],
			rotateMaxWeight: [this.rotateMaxWeight, this.formValidators],
			rotateMaxLengthToHeightAspectRatio: [this.rotateMaxLengthToHeightAspectRatio, this.formValidators],
		});
		this.sortingPriorityForm = this.fb.group({
			sortingElementLengthPriority: [this.sortingElementLengthPriority, this.sortingPriorityFormValidators],
			sortingPositionWeightPriority: [this.sortingPositionWeightPriority, this.sortingPriorityFormValidators],
			sortingBatchNumberPriority: [this.sortingBatchNumberPriority, this.sortingPriorityFormValidators],
			sortingProductionSequenceNumberPriority: [this.sortingProductionSequenceNumberPriority, this.sortingPriorityFormValidators],
		});
		this.poolingPriorityForm = this.fb.group({
			poolingPalletPriority: [this.poolingPalletPriority, this.poolingPriorityFormValidators],
			poolingBatchPriority: [this.poolingBatchPriority, this.poolingPriorityFormValidators],
			poolingOrderPriority: [this.poolingOrderPriority, this.poolingPriorityFormValidators],
			poolingPartitionPriority: [this.poolingPartitionPriority, this.poolingPriorityFormValidators],
			poolingPalletDimensionPriority: [this.poolingPalletDimensionPriority, this.poolingPriorityFormValidators],
			poolingPalletTypePriority: [this.poolingPalletTypePriority, this.poolingPriorityFormValidators],
			poolingGlassFittingPriority: [this.poolingGlassFittingPriority, this.poolingPriorityFormValidators],
		});
	}

	public get controls() {
		return this.form.controls;
	}

	public get sortingPriorityControls() {
		return this.sortingPriorityForm.controls;
	}

	public get poolingPriorityControls() {
		return this.poolingPriorityForm.controls;
	}

	public clear(): void {
		this.isLoadingConfiguration = false;
		this.isUpdatingConfiguration = false;

		this.form.reset();
		this.sortingPriorityForm.reset();
		this.poolingPriorityForm.reset();

		this.tableSource.data = [];
		this.layersTableSource.data = [];
		this.palletTypes = [];
		this.selectedPalletType = null;
		this.elementPalletTypes = [];
		this.filteredAssociatedTypes = [];

		this.allowStackingWithoutSupportingBase = true;
		this.palletElementPadding = 0;
		this.palletMaxHeight = 0;
		this.palletMaxLayers = 0;
		this.palletMaxRows = 0;
		this.rotateMaxLengthToHeightAspectRatio = 0;
		this.rotateMaxWeight = 0;
		this.rotateMinRatioSingleElementOnly = true;

		this.sortingElementLength = false;
		this.sortingElementLengthOrder = false;
		this.sortingElementLengthPriority = null;
		this.sortingPositionWeight = false;
		this.sortingPositionWeightOrder = false;
		this.sortingPositionWeightPriority = null;
		this.sortingBatchNumber = false;
		this.sortingBatchNumberOrder = false;
		this.sortingBatchNumberPriority = null;
		this.sortingProductionSequenceNumber = false;
		this.sortingProductionSequenceNumberOrder = false;
		this.sortingProductionSequenceNumberPriority = null;

		this.poolingPallet = false;
		this.poolingPalletUnspecified = false;
		this.poolingPalletPriority = null;
		this.poolingBatch = false;
		this.poolingBatchUnspecified = false;
		this.poolingBatchPriority = null;
		this.poolingOrder = false;
		this.poolingOrderUnspecified = false;
		this.poolingOrderPriority = null;
		this.poolingPartition = false;
		this.poolingPartitionUnspecified = false;
		this.poolingPartitionPriority = null;
		this.poolingPalletDimension = false;
		this.poolingPalletDimensionUnspecified = false;
		this.poolingPalletDimensionPriority = null;
		this.poolingPalletType = false;
		this.poolingPalletTypeUnspecified = false;
		this.poolingPalletTypePriority = null;
		this.poolingGlassFitting = false;
		this.poolingGlassFittingUnspecified = false;
		this.poolingGlassFittingPriority = null;

		this.balancingMethod = '-1';
	}

	private sortingNotUnique(target: FormControl): { error: string } | null {
		return this.notUnique(target, this.sortingPriorityForm);
	}

	private poolingNotUnique(target: FormControl): { error: string } | null {
		return this.notUnique(target, this.poolingPriorityForm);
	}

	private notUnique(target: FormControl, form: FormGroup): { error: string } | null {
		if (!target.value) {
			return null;
		}
		const targetValue = String(target.value);
		const controls = Object.entries(form.controls);
		for (const [_, control] of controls) {
			if (control !== target) {
				const controlValue = String(control.value);
				if (controlValue === '' && targetValue === '') {
					return null;
				}
				if (controlValue === targetValue) {
					return {
						error: 'error',
					};
				}
			}
		}
		return null;
	}

	/** Huge gotcha! If all FormFields in a Form are disabled, the Form.valid returns false. So we iterate over each FormField to check if they are disabled. */
	public get isSortingFormValid(): boolean {
		for (const { control } of this.sortingFormFieldsLookup) {
			if (control.enabled && control.invalid) {
				return false;
			}
		}
		return true;
	}

	/** Huge gotcha! If all FormFields in a Form are disabled, the Form.valid returns false. So we iterate over each FormField to check if they are disabled. */
	public get isPoolingFormValid(): boolean {
		for (const { control } of this.poolingFormFieldsLookup) {
			if (control.enabled && control.invalid) {
				return false;
			}
		}
		return true;
	}

	public get areFormsValid(): boolean {
		return this.form.valid && this.isSortingFormValid && this.isPoolingFormValid;
	}

	public get isSaveButtonDisabled(): boolean {
		return !this.areFormsValid || this.isUpdatingConfiguration;
	}

	public get isResetButtonDisabled(): boolean {
		return this.isUpdatingConfiguration;
	}
}
